import React, { useEffect, useRef, useState } from 'react';
import { ColorPicker } from '~/design-system/elements/ColorPicker/ColorPicker';
import {
  hexToHsv,
  hsvToHex,
  hueToColor,
  normalizeHex,
  parseCssColor,
  toCssColor,
  type Hsv,
} from '~/design-system/elements/ColorPicker/colorMath';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './TextColorPanel.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/** Fixed, like every other v1.5 menu width. Sized by the square inside it, not by its longest row. */
export const TEXT_COLOR_PANEL_WIDTH = 216;

export interface TextColorPanelProps {
  /**
   * The colour the text is CURRENTLY drawn in — any CSS colour, because it comes from
   * `getComputedStyle` and that answers `rgb(17, 17, 17)` however the author wrote it.
   */
  color: string;
  /**
   * A CSS colour, on every drag step rather than only the release: `#rrggbb` while it is opaque,
   * `rgba(...)` once it is not. See the note on live changes below.
   */
  onColor: (color: string) => void;
  theme?: HeaderTheme;
  className?: string;
}

/**
 * THE TEXT COLOUR PICKER, hung off the colour chip on `TextToolbar`.
 *
 * A saturation square, a hue strip, and a hex field. That is the whole instrument, and the omissions
 * are the design:
 *
 * - **No gradient tabs.** `color` takes no gradient. `ColorWheel` offers four modes because it fills
 *   shapes; three of them would be dead controls here.
 * - **No swatches or recents.** A swatch row is only worth its space when the colours in it are the
 *   DOCUMENT'S — and the document's palette is not something this bar can see today. An arbitrary
 *   row of house colours on someone else's artifact is decoration.
 *
 * ## Opacity IS here, and it was not
 *
 * It was left out on the argument that the element already has its own `opacity`, so a second alpha
 * hidden inside the colour gives you text that is faint for two reasons and can only be fixed by
 * finding both. That argument is still true and it was still the wrong call, because it ignored where
 * these colours COME FROM: artifact body copy at `rgba(253, 252, 252, 0.6)` is ordinary, and a panel
 * that could only emit hex turned it opaque the moment you nudged the hue. Refusing to show a value
 * the document is already using does not simplify anything — it just makes the control destructive
 * (Beka, 2026-09-06: "it should read exact data from generated artifact text").
 *
 * The two alphas stay distinguishable because this one is only ever the COLOUR'S: the strip reads and
 * writes the alpha channel, never the element's `opacity` property, which has no control on this bar
 * at all.
 *
 * ## Why it holds HSV and not the hex
 *
 * Hue does not survive a round trip through a grey: black, white and every grey have zero
 * saturation, so `hexToHsv` answers hue 0 for all of them. A panel that re-derived its position from
 * the hex on every change would snap the strip back to red the moment the user dragged to the bottom
 * of the square, under their own hand. So HSV is the state, hex is the output, and the incoming
 * `color` only re-seeds it when it changed for a reason that was not us — see `emittedRef`.
 *
 * ## Live, not on release
 *
 * `onColor` fires on every step of a drag. The point of a colour picker over an artifact is watching
 * the headline change while you move, and the host is already debouncing what it persists — so the
 * document repaints continuously and one revision is written when the drag settles.
 */
export function TextColorPanel({ color, onColor, theme: themeProp, className }: TextColorPanelProps) {
  const theme = useV15Theme(themeProp);

  const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(parseCssColor(color).hex));
  /**
   * The colour's own alpha, 0-1, held beside the HSV for the same reason: it is part of the value the
   * user is dragging and re-deriving it from the host on every step would fight the drag.
   */
  const [alpha, setAlpha] = useState(() => parseCssColor(color).alpha);

  /**
   * The last hex this panel sent out.
   *
   * The host is the source of truth for the colour and hands it straight back, so without this the
   * panel would treat its OWN output as an external change and re-seed from it — undoing the whole
   * reason it holds HSV. Anything that does not match this came from somewhere else (another
   * control, an undo, a different element) and is worth adopting.
   */
  const emittedRef = useRef<string | null>(null);

  /**
   * What is in the hex field WHILE it is being typed, or null when it is showing the real value.
   *
   * A controlled field bound straight to `hex` cannot be typed into: the first keystroke leaves an
   * unparseable string, the parse fails, and the field snaps back to the old colour mid-word. So the
   * draft is held here and the colour is committed on the first value that parses.
   */
  const [draft, setDraft] = useState<string | null>(null);

  const hex = hsvToHex(hsv[0], hsv[1], hsv[2]);

  useEffect(() => {
    const incoming = parseCssColor(color);
    if (toCssColor(incoming.hex, incoming.alpha) === emittedRef.current) return;
    emittedRef.current = null;
    setHsv(hexToHsv(incoming.hex));
    setAlpha(incoming.alpha);
    setDraft(null);
    // `hex` is deliberately out: comparing against the panel's own current value would make this an
    // echo of itself. The only question here is whether the HOST's colour moved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color]);

  const emit = (next: Hsv, nextAlpha = alpha) => {
    setHsv(next);
    setAlpha(nextAlpha);
    setDraft(null);
    const value = toCssColor(hsvToHex(next[0], next[1], next[2]), nextAlpha);
    emittedRef.current = value;
    onColor(value);
  };

  const commitDraft = (raw: string) => {
    const parsed = normalizeHex(raw);
    if (!parsed) {
      // Not a colour. Drop the draft rather than correcting it — the field goes back to showing what
      // the text actually is, which is the honest answer to "that was not a colour".
      setDraft(null);
      return;
    }
    // The alpha rides along untouched: the field is a HEX field, six digits, and typing one is a
    // statement about the colour rather than about how transparent it is.
    emit(hexToHsv(parsed));
  };

  return (
    <div
      className={cn(themes[theme], styles.panel, className)}
      // The panel is a dialog's worth of controls, not a menu of rows: nothing in here is an
      // `option`, and announcing it as a menu would promise arrow-key navigation it does not have.
      role="group"
      aria-label="Text colour"
    >
      <ColorPicker
        variant="saturation"
        className={styles.square}
        hueColor={hueToColor(hsv[0])}
        saturation={hsv[1]}
        brightness={hsv[2]}
        // The dot is filled with the colour it is standing on, so it reads as a sample rather than as
        // a cursor — and it keeps its own white rim against both ends of the square.
        indicatorColor={hex}
        onChange={(s, v) => emit([hsv[0], s, v])}
      />

      <ColorPicker
        variant="hue"
        className={styles.hue}
        value={hsv[0]}
        indicatorColor={hueToColor(hsv[0])}
        onChange={(h) => emit([h, hsv[1], hsv[2]])}
      />

      <ColorPicker
        variant="opacity"
        className={styles.hue}
        value={alpha * 100}
        color={hex}
        indicatorColor={hex}
        onChange={(next) => emit(hsv, Math.max(0, Math.min(1, next / 100)))}
      />

      <div className={styles.field}>
        <span
          className={styles.swatch}
          // The two genuine runtime values in the file: an arbitrary colour and its alpha, so no
          // stylesheet can hold them. The swatch paints the colour AS IT WILL LAND, alpha included,
          // over a checker — a 40% white shown solid would be the same lie the panel was fixed for.
          style={
            { '--hv15-panel-swatch': toCssColor(hex, alpha) } as React.CSSProperties
          }
          aria-hidden
        />
        <input
          className={styles.hex}
          type="text"
          inputMode="text"
          spellCheck={false}
          autoComplete="off"
          aria-label="Hex colour"
          value={draft ?? hex}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={(event) => commitDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitDraft(event.currentTarget.value);
              return;
            }
            if (event.key === 'Escape' && draft !== null) {
              // Abandon the half-typed value without closing the panel — the bar's own Escape
              // handler would otherwise shut the menu out from under someone mid-word. Only while
              // there IS a draft: with the field showing the real colour there is nothing to
              // abandon, so Escape means what it means everywhere else and closes the menu.
              event.preventDefault();
              event.stopPropagation();
              setDraft(null);
            }
          }}
          /**
           * THE ONE PLACE ON THIS BAR THAT IS ALLOWED TO TAKE FOCUS.
           *
           * The toolbar root kills mousedown's default so pressing it cannot blur the caret inside
           * the artifact's iframe — that guard is what makes the whole bar usable. A text field is
           * the exception that proves it: an input you cannot focus is an input you cannot type in.
           *
           * Stopping propagation here (rather than the root skipping inputs) keeps the rule readable
           * in one direction: the root still says "nothing on this bar takes focus", and the single
           * control that must have it says so itself, at the point where it matters.
           *
           * Losing the artifact's caret to this field is harmless in a way it is not for the other
           * controls: the bridge commits on blur, the element stays SELECTED, and every control here
           * addresses the element rather than the caret.
           */
          onMouseDown={(event) => event.stopPropagation()}
        />
      </div>
    </div>
  );
}
